import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Heart, Check } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewsletterSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { 
        email, 
        first_name: firstName 
      });
      setIsSubscribed(true);
      toast.success("Welcome to My Tribe!");
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info("You're already part of the tribe!");
        setIsSubscribed(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="my-tribe"
      ref={ref}
      className="py-24 md:py-40 px-6 md:px-12 bg-[#F5F0EA]"
      data-testid="newsletter-section"
    >
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {/* Section Label */}
            <div className="flex items-center gap-3 mb-6">
              <Heart size={20} className="text-sand-dark" fill="#A89A80" strokeWidth={1.5} />
              <p className="font-body text-xs tracking-[0.3em] uppercase text-sand-dark">
                Join the Community
              </p>
            </div>

            {/* Title */}
            <h2 
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-graphite-dark mb-2"
              data-testid="newsletter-title"
            >
              My Tribe
            </h2>
            <p className="font-accent text-xl md:text-2xl italic text-graphite/70 mb-8">
              A Weekly Note from Elise
            </p>

            {/* Subheading */}
            <div className="mb-8">
              <p className="font-body text-lg text-graphite/60 leading-relaxed">
                Simple insights.<br />
                Strong habits.<br />
                A better tomorrow.
              </p>
            </div>

            {/* Description */}
            <p className="font-body text-base text-graphite/70 leading-relaxed mb-6 max-w-lg">
              This is my weekly space to share what I'm learning, what I'm refining, 
              and what I'm reminding myself of — in hopes that it helps you too.
            </p>

            <p className="font-body text-base text-graphite/70 leading-relaxed max-w-lg">
              Each week, I'll send something thoughtful — something to strengthen your body, 
              steady your mind, and inspire your next step.
            </p>

            {/* What You'll Get */}
            <div className="mt-10 space-y-3">
              <p className="font-body text-xs tracking-[0.2em] uppercase text-graphite/50 mb-4">
                What to expect
              </p>
              {[
                "A personal reflection from me",
                "One practical health or recovery insight",
                "A mindset or lifestyle shift",
                "A weekly question for reflection"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-sand-dark" />
                  <span className="font-body text-sm text-graphite/60">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right Column - Signup Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:pl-8"
          >
            <div className="bg-white p-8 md:p-12 border border-sand/30">
              <AnimatePresence mode="wait">
                {!isSubscribed ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3 className="font-heading text-xl tracking-wide uppercase text-graphite-dark mb-2">
                      Join My Tribe
                    </h3>
                    <p className="font-body text-sm text-graphite/50 mb-8">
                      Free weekly inspiration delivered to your inbox
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* First Name */}
                      <div>
                        <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block">
                          First Name (optional)
                        </label>
                        <input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="Your first name"
                          className="w-full bg-transparent border-b border-graphite/20 focus:border-sand-dark outline-none py-3 text-graphite-dark placeholder:text-graphite/30 font-body transition-colors"
                          data-testid="newsletter-firstname"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="font-body text-xs tracking-[0.15em] uppercase text-graphite/50 mb-2 block">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          required
                          className="w-full bg-transparent border-b border-graphite/20 focus:border-sand-dark outline-none py-3 text-graphite-dark placeholder:text-graphite/30 font-body transition-colors"
                          data-testid="newsletter-email"
                        />
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={isSubmitting || !email}
                        className={`w-full py-4 font-heading tracking-widest uppercase transition-all duration-300 mt-4 ${
                          email && !isSubmitting
                            ? "bg-graphite-dark text-bone hover:bg-black"
                            : "bg-graphite/20 text-graphite/40 cursor-not-allowed"
                        }`}
                        data-testid="newsletter-submit"
                      >
                        {isSubmitting ? "Joining..." : "Join My Tribe"}
                      </button>
                    </form>

                    <p className="font-body text-xs text-graphite/40 mt-6 text-center">
                      No spam. Unsubscribe anytime. Just good energy.
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="text-center py-8"
                  >
                    {/* Success Icon */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", duration: 0.6, delay: 0.1 }}
                      className="w-16 h-16 rounded-full bg-sand/30 flex items-center justify-center mx-auto mb-6"
                    >
                      <Heart size={32} className="text-sand-dark" fill="#A89A80" strokeWidth={1.5} />
                    </motion.div>

                    <motion.h3
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="font-heading text-2xl tracking-wide uppercase text-graphite-dark mb-4"
                    >
                      You're In
                    </motion.h3>

                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      className="font-body text-graphite/70 leading-relaxed mb-6"
                    >
                      Each week, I'll send something thoughtful — something to strengthen 
                      your body, steady your mind, and inspire your next step.
                    </motion.p>

                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 }}
                      className="font-accent text-lg italic text-graphite/60"
                    >
                      Welcome to the tribe.
                    </motion.p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Community Values */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-8 text-center"
            >
              <p className="font-body text-xs text-graphite/40 leading-relaxed">
                For women who value growth, health, and positive community.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
